<template>
  <div>
    <BlBackdrop
      title="Edit Package Item"
      label="Update"
      :submitLoading="submitLoading"
      :disabled="isDisabledSubmit"
      @backdrop-close="$emit('close-modal')"
      @backdrop-action="onSubmit"
    >
      <div
        v-if="fetchLoading"
        class="h-100 grid place-items-center"
        :style="{ width: '40rem' }"
      >
        <div class="blum-loader-sm"></div>
      </div>
      <div
        v-else
        class="flex flex-col gap-4 overflow-y-auto"
        :style="{ width: '40rem', maxHeight: 'calc(100vh - 20rem)' }"
      >
        <div class="px-8 flex flex-col gap-4">
          <BlInputText :input="nameInput" @changed="inputHandler" />
          <BlInputText :input="descriptionInput" @changed="inputHandler" />
          <BlInputText
            :input="standardRetailPriceInput"
            @changed="inputHandler"
          />

          <div
            v-if="isShowSRPChanged"
            class="py-2 px-3 flex items-start gap-2 bg-cornflower-200 rounded "
          >
            <BlIcon icon="info" color="cornflower-8" class="h-6 w-6" />
            <div>
              <BlTextBody2
                text="This item is associated with"
                class="text-cornflower-800"
              />
              <BlTextBody2
                :text="creditPackageItem.packages.length"
                class="text-cornflower-800 font-bold"
              />
              <BlTextBody2
                text="package(s). Any changes to its SRP will not influence the SRP of the package(s). To adjust the SRP in the packages, you must edit them manually."
                class="text-cornflower-800"
              />
            </div>
          </div>
        </div>

        <div class="w-full h-5 bg-grey-100"></div>

        <div class="px-8 flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <BlTextSubtitle1 text="Provider" />
            <BlTextBody2
              text="Specify which therapists can provide this service. You can choose to make it available for everyone or limit it to selected therapists for more control over credit usage."
            />
          </div>

          <div
            v-if="isShowAvailableForEveryoneProvider"
            class="py-2 px-3 flex items-start gap-2 bg-cornflower-200 rounded "
          >
            <BlIcon icon="info" color="cornflower-8" class="h-6 w-6" />
            <div>
              <BlTextBody2
                text="The provider for this item cannot be changed to 'Restricted' as it is currently being used in"
                class="text-cornflower-800"
              />
              <BlTextBody2
                :text="creditPackageItem.total_active_appointments"
                class="text-cornflower-800 font-bold"
              />
              <BlTextBody2 text="appointment(s)." class="text-cornflower-800" />
            </div>
          </div>

          <div
            v-if="isShowRestrictedProvider"
            class="py-2 px-3 flex items-start gap-2 bg-cornflower-200 rounded "
          >
            <BlIcon icon="info" color="cornflower-8" class="h-6 w-6" />
            <div>
              <BlTextBody2
                text="The following therapist(s) has active appointments using this item and cannot be removed from the list until all appointments are concluded:"
                class="text-cornflower-800"
              />
              <div
                v-for="user in creditPackageItem.impacted_therapists"
                :key="user.id"
                class="px-2 flex items-center gap-2"
              >
                <div
                  class="h-1 w-1 flex-shrink-0 bg-cornflower-800 rounded"
                ></div>
                <BlTextBody2
                  :text="user.name"
                  class="text-cornflower-800 font-bold"
                />
              </div>
              <div>
                <BlTextBody2
                  text="Total appointments impacted:"
                  class="text-cornflower-800"
                />
                <BlTextBody2
                  :text="creditPackageItem.total_active_appointments"
                  class="text-cornflower-800 font-bold"
                />
              </div>
            </div>
          </div>

          <div
            v-for="opt in providerOptions"
            :key="opt.value"
            class="flex gap-4 cursor-pointer"
            :class="{ 'pointer-events-none opacity-75': opt.disabled }"
            @click="providerInput = opt.value"
          >
            <BlInputRadio
              :checked="providerInput === opt.value"
              :disabled="opt.disabled"
              name="provider"
            />
            <BlTextBody2 :text="opt.label" />
          </div>
          <div
            v-if="providerInput === 'restricted'"
            class="py-2 px-2 flex flex-col gap-2 bg-slate-200 rounded"
          >
            <div class="flex items-center justify-between">
              <BlTextBody2
                :text="`${usersInput.length} therapist(s) selected`"
              />
              <BlInputButton
                type="outlined"
                dense
                label="Add therapist"
                @clicked="usersMultiSelectOpened = true"
              />
            </div>
            <div v-if="usersInput.length" class="flex flex-col">
              <div
                v-for="user in usersInput"
                :key="user.id"
                class="py-2 flex items-center justify-between gap-4 border-b border-slate-400"
              >
                <BlTextSubtitle3
                  v-if="!user.name"
                  title="Deleted user"
                  text="Deleted user"
                  class="truncate italic"
                />
                <BlTextSubtitle3
                  v-else
                  :title="user.name"
                  :text="user.name"
                  class="truncate"
                />
                <BlTextBody2 :text="user.email" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="absolute left-8 -bottom-14 z-10 flex items-center gap-4">
        <BlTooltip>
          <template v-if="creditPackageItem.in_use" v-slot:tooltip>
            <div class="w-48">
              You can't delete this item because it is currently being used in
              active appointments and/or package.
            </div>
          </template>
          <BlInputButton
            type="outlined-error"
            label="Delete item"
            :disabled="creditPackageItem.in_use"
            @clicked="deleteOpened = true"
          />
        </BlTooltip>
        <BlTooltip v-if="creditPackageItem.status === 'archived'">
          <BlInputButton
            type="outlined"
            label="Reactivate item"
            :loading="submitLoading"
            @clicked="onReactivate"
          />
        </BlTooltip>
        <BlTooltip v-else>
          <BlInputButton
            type="outlined"
            label="Archive item"
            :loading="submitLoading"
            @clicked="archiveOpened = true"
          />
        </BlTooltip>
      </div>
    </BlBackdrop>

    <UsersMultiSelect
      v-if="usersMultiSelectOpened"
      :list="usersInput"
      :disabledList="creditPackageItem.impacted_therapists"
      @change="usersInput = $event"
      @close-modal="usersMultiSelectOpened = false"
    />

    <BlBackdrop
      v-if="confirmProviderChangeOpened"
      title="Confirm provider change"
      description="By changing this provider to ''Available for Everyone'', this item will be accessible to all therapists for use in credit transactions. Any active restrictions will be removed, and all therapists will have access to this item. Are you sure you want to proceed?"
      label="Confirm"
      maxWidth="40rem"
      :submitLoading="submitLoading"
      :disabled="isDisabledSubmit"
      @backdrop-close="confirmProviderChangeOpened = false"
      @backdrop-action="onSubmit"
    />

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete item"
      :description="
        `You are about to delete <b>${creditPackageItem.name}</b>. Are you sure you want to proceed? This action is irreversible.`
      "
      label="Delete"
      type="error"
      :submitLoading="submitLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onDelete"
    />

    <ArchivePackageItemModal
      v-if="archiveOpened"
      :creditPackageItem="creditPackageItem"
      @close-modal="archiveOpened = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayPrice } from "@/plugins/helper.js";

import UsersMultiSelect from "../../modal/UsersMultiSelect.vue";
import ArchivePackageItemModal from "./ArchivePackageItemModal.vue";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlTooltip from "../../BlTooltip.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "EditPackageItemModal",
  components: {
    UsersMultiSelect,
    ArchivePackageItemModal,
    //
    BlBackdrop,
    BlTextSubtitle1,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlTextBody3,
    BlTooltip,
    BlIcon,
    BlInputText,
    BlInputRadio,
    BlInputButton
  },
  props: {
    creditPackageItemId: { type: Number }
  },
  data() {
    return {
      fetchLoading: false,
      submitLoading: false,
      nameInput: {
        label: "Name",
        asterisk: true,
        placeholder: "Type the name",
        model: ""
      },
      descriptionInput: {
        label: "Description",
        placeholder: "Type the description",
        textarea: true,
        model: ""
      },
      standardRetailPriceInput: {
        label: "SRP (Standard Retail Price)",
        inputType: "number",
        model: "0",
        prefix: "",
        prefixSpace: "3rem",
        suffix: "per credit"
      },
      providerInput: "available_for_everyone", // available_for_everyone or restricted
      //
      usersMultiSelectOpened: false,
      usersInput: [],
      //
      confirmProviderChangeOpened: false,
      deleteOpened: false,
      archiveOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentCenter", "creditPackageItem"]),
    providerOptions() {
      return [
        {
          value: "available_for_everyone",
          label: "Available for everyone",
          disabled: false
        },
        {
          value: "restricted",
          label: "Restricted",
          disabled: this.isShowAvailableForEveryoneProvider
        }
      ];
    },
    isShowSRPChanged() {
      const packagesUsed = (this.creditPackageItem.packages || []).length;
      const oldSRP = this.creditPackageItem.standard_retail_price;
      const newSRP = this.standardRetailPriceInput.model;
      return packagesUsed > 0 && oldSRP !== newSRP;
    },
    isShowAvailableForEveryoneProvider() {
      const appointments = this.creditPackageItem.total_active_appointments;
      return (
        this.creditPackageItem.provider === "available_for_everyone" &&
        appointments > 0
      );
    },
    isShowRestrictedProvider() {
      const impacted = (this.creditPackageItem.impacted_therapists || [])
        .length;
      const appointments = this.creditPackageItem.total_active_appointments;
      return (
        this.creditPackageItem.provider === "restricted" &&
        impacted > 0 &&
        appointments > 0
      );
    },
    isDisabledSubmit() {
      if (!this.nameInput.model) return true;
      // if (!this.standardRetailPriceInput.model) return true;
      // if (this.standardRetailPriceInput.model === "0") return true;
      return false;
    }
  },
  watch: {},
  async mounted() {
    if (this.currentCenter) {
      this.standardRetailPriceInput.prefix = this.currentCenter.currency_sym;
    }

    this.fetchLoading = true;
    const { success, data } = await this.getCreditPackageItem({
      id: this.creditPackageItemId
    });
    this.fetchLoading = false;
    if (!success) return;

    this.nameInput.model = data.name;
    this.descriptionInput.model = data.description;
    this.standardRetailPriceInput.model = data.standard_retail_price;
    this.providerInput = data.provider;
    this.usersInput = data.users;
  },
  methods: {
    ...mapActions([
      "getCreditPackageItem",
      "updateCreditPackageItem",
      "deleteCreditPackageItem"
    ]),
    displayPrice,
    priceFormats(price = 0) {
      const settings = this.currentCenter.currency_settings;
      return this.displayPrice({ settings, price });
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async onSubmit() {
      const params = {
        id: this.creditPackageItemId,
        package_item: {
          name: this.nameInput.model,
          description: this.descriptionInput.model,
          standard_retail_price: this.standardRetailPriceInput.model,
          provider: this.providerInput,
          user_ids: []
        }
      };

      if (this.providerInput === "restricted") {
        params.package_item.user_ids = this.usersInput.map(i => i.id);
      }

      if (!this.confirmProviderChangeOpened) {
        if (
          this.creditPackageItem.provider === "restricted" &&
          this.providerInput === "available_for_everyone"
        ) {
          this.confirmProviderChangeOpened = true;
          return;
        }
      }

      this.submitLoading = true;
      const { success } = await this.updateCreditPackageItem(params);
      this.submitLoading = false;
      if (!success) return;
      this.$emit("close-modal");
    },
    async onDelete() {
      const params = this.creditPackageItem;

      this.submitLoading = true;
      const { success } = await this.deleteCreditPackageItem(params);
      this.submitLoading = false;
      if (!success) return;
      this.$emit("close-modal");
    },
    async onReactivate() {
      const params = {
        id: this.creditPackageItemId,
        package_item: { status: "active" },
        title: "Item activated",
        message: `Success! The <b>${this.creditPackageItem.name}</b> has been activated.`
      };

      this.submitLoading = true;
      await this.updateCreditPackageItem(params);
      this.submitLoading = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/center/packageItem/EditPackageItemModal.vue